import type {
  ThemeProps,
  HeroSlotData,
  BodySlotData,
  GalleryItem,
  FooterSlotData,
} from '@/types/theme';

export const FIXTURE_HERO: HeroSlotData = {
  title: 'Beyond Africa — A Photo Journey',
  subtitle: "Exploring the continent's hidden stories",
  coverImage: 'https://placehold.co/1280x720/1E3A5F/ffffff?text=Cover+Image',
  author: 'Amara Diallo',
  authorAvatar: 'https://placehold.co/64x64/2DD4BF/ffffff?text=AD',
  publishedAt: '2026-05-01T08:00:00Z',
  updatedAt: '2026-05-10T12:00:00Z',
  readingTimeMinutes: 8,
  breadcrumbs: [
    { label: 'Home', href: '/' },
    { label: 'Travel', href: '/travel' },
    { label: 'Africa', href: '/travel/africa' },
  ],
};

const BODY_HTML = `<p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.</p>
<p>Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.</p>
<h2 id="the-journey">The Journey</h2>
<p>Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo.</p>
<h3 id="landscapes">Landscapes</h3>
<p>Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt. Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet.</p>
<blockquote><p>The landscape stretches beyond the horizon, an endless canvas of ochre and gold, interrupted only by the silhouettes of acacia trees standing sentinel against a burning sky.</p></blockquote>
<h3 id="people">People</h3>
<p>At vero eos et accusamus et iusto odio dignissimos ducimus qui blanditiis praesentium voluptatum deleniti atque corrupti quos dolores et quas molestias excepturi sint occaecati cupiditate non provident.</p>
<p>Similique sunt in culpa qui officia deserunt mollitia animi, id est laborum et dolorum fuga. Et harum quidem rerum facilis est et expedita distinctio. Nam libero tempore, cum soluta nobis est eligendi optio cumque nihil impedit quo minus.</p>
<h2 id="conclusion">Conclusion</h2>
<p>Temporibus autem quibusdam et aut officiis debitis aut rerum necessitatibus saepe eveniet ut et voluptates repudiandae sint et molestiae non recusandae. Itaque earum rerum hic tenetur a sapiente delectus, ut aut reiciendis voluptatibus maiores.</p>
<p>Quis autem vel eum iure reprehenderit qui in ea voluptate velit esse quam nihil molestiae consequatur, vel illum qui dolorem eum fugiat quo voluptas nulla pariatur.</p>`;

export const FIXTURE_BODY: BodySlotData = {
  html: BODY_HTML,
  tableOfContents: [
    { id: 'introduction', label: 'Introduction', level: 1 },
    { id: 'the-journey', label: 'The Journey', level: 1 },
    { id: 'landscapes', label: 'Landscapes', level: 2 },
    { id: 'people', label: 'People', level: 2 },
    { id: 'conclusion', label: 'Conclusion', level: 1 },
  ],
};

export const FIXTURE_GALLERY: GalleryItem[] = [
  {
    src: 'https://placehold.co/800x600/1E3A5F/ffffff?text=Photo+1',
    alt: 'Savanna at dawn',
    caption: 'The savanna stretches to the horizon at first light',
    credit: 'Amara Diallo',
  },
  {
    src: 'https://placehold.co/800x600/2DD4BF/ffffff?text=Photo+2',
    alt: 'Market scene',
    caption: 'A bustling morning market in the old town',
    credit: 'Amara Diallo',
  },
  {
    src: 'https://placehold.co/800x600/F59E0B/ffffff?text=Photo+3',
    alt: 'Mountain village',
    caption: 'A village perched high in the Atlas Mountains',
    credit: 'Amara Diallo',
  },
  {
    src: 'https://placehold.co/800x600/6B7280/ffffff?text=Photo+4',
    alt: 'River delta',
    caption: 'The river delta seen from above',
    credit: 'Amara Diallo',
  },
  {
    src: 'https://placehold.co/800x600/7C3AED/ffffff?text=Photo+5',
    alt: 'Desert dunes',
    caption: 'Sand dunes shifting in the evening light',
    credit: 'Amara Diallo',
  },
  {
    src: 'https://placehold.co/800x600/DC2626/ffffff?text=Photo+6',
    alt: 'Coastal cliffs',
    caption: 'Dramatic coastal cliffs at sunset',
    credit: 'Amara Diallo',
  },
];

export const FIXTURE_FOOTER: FooterSlotData = {
  authorBio:
    'Amara Diallo is a documentary photographer and writer based between Dakar and Lisbon. Her work focuses on the intersection of landscape, memory, and cultural identity.',
  authorName: 'Amara Diallo',
  authorAvatar: 'https://placehold.co/64x64/2DD4BF/ffffff?text=AD',
  relatedArticles: [
    {
      title: 'The Sahel in Transition',
      href: '/sahel-transition',
      thumbnailSrc: 'https://placehold.co/200x150/1E3A5F/ffffff?text=Related+1',
    },
    {
      title: 'East African Highlands: A Visual Essay',
      href: '/east-africa-highlands',
      thumbnailSrc: 'https://placehold.co/200x150/2DD4BF/ffffff?text=Related+2',
    },
    {
      title: 'Urban Stories: Nairobi at Night',
      href: '/nairobi-night',
      thumbnailSrc: 'https://placehold.co/200x150/F59E0B/ffffff?text=Related+3',
    },
  ],
  tags: ['africa', 'travel', 'photo', 'documentary', 'landscape'],
  shareUrl: 'https://beyond-africa.my-investments.io/photo-journey',
};

export const FIXTURE_CONTENT: ThemeProps = {
  hero: FIXTURE_HERO,
  body: FIXTURE_BODY,
  gallery: FIXTURE_GALLERY,
  footer: FIXTURE_FOOTER,
};
